import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type { Process } from './types';
import { at, pad } from './support';

/**
 * The L1–L4 process catalogue.
 *
 * Levels 1 and 2 are authored: they are what a chief operating officer would
 * recognise as "how an airline runs", and they anchor the process column of the
 * mapping studio. Levels 3 and 4 are generated from per-L2 step banks, because
 * three hundred and twenty task-level steps is a data-entry exercise, not a
 * design one — and because the mapping screens need enough volume to be
 * exercised honestly.
 */

interface L1Seed {
  code: string;
  name: string;
  description: string;
  ownerPersonCode: string;
  l2: readonly L2Seed[];
}

interface L2Seed {
  code: string;
  name: string;
  /** Authored L3 steps where the demo needs recognisable detail. */
  steps?: readonly string[];
}

/** Fallback L3 verbs, used where a bespoke step list would add nothing. */
const GENERIC_STEPS = ['Plan', 'Prepare', 'Execute', 'Validate', 'Monitor', 'Report on'];

/** L4 task suffixes. Deliberately mundane — task level is where work is logged. */
const L4_TASKS = [
  'Capture inputs',
  'Apply business rules',
  'Resolve exceptions',
  'Obtain approval',
  'Publish outcome',
  'Notify stakeholders',
  'Record evidence',
];

const L1_SEEDS: readonly L1Seed[] = [
  {
    code: 'PRC-1',
    name: 'Plan the Network & Schedule',
    description: 'Decide where and when the airline flies, and with which aircraft.',
    ownerPersonCode: 'PPL-0024',
    l2: [
      { code: 'PRC-1.1', name: 'Develop Network Strategy' },
      {
        code: 'PRC-1.2',
        name: 'Build the Schedule',
        steps: [
          'Draft Seasonal Schedule',
          'Assign Fleet to Schedule',
          'Validate Schedule Feasibility',
          'Publish Schedule',
        ],
      },
      { code: 'PRC-1.3', name: 'Manage Slots & Airport Access' },
      { code: 'PRC-1.4', name: 'Manage Codeshare & Partner Schedules' },
      { code: 'PRC-1.5', name: 'Manage Schedule Change' },
    ],
  },
  {
    code: 'PRC-2',
    name: 'Sell & Distribute',
    description: 'Price, merchandise and sell inventory across every channel.',
    ownerPersonCode: 'PPL-0028',
    l2: [
      {
        code: 'PRC-2.1',
        name: 'Manage Fares & Pricing',
        steps: [
          'File Fares',
          'Set Bid Prices',
          'Monitor Competitive Fares',
          'Approve Promotional Fares',
        ],
      },
      { code: 'PRC-2.2', name: 'Manage Inventory & Availability' },
      {
        code: 'PRC-2.3',
        name: 'Construct & Present Offers',
        steps: [
          'Search Availability',
          'Assemble Offer',
          'Personalise Offer',
          'Return Offer to Channel',
        ],
      },
      {
        code: 'PRC-2.4',
        name: 'Create & Manage Orders',
        steps: [
          'Create Booking',
          'Take Payment',
          'Issue Ticket & Documents',
          'Confirm to Customer',
        ],
      },
      { code: 'PRC-2.5', name: 'Distribute through Partners & Agencies' },
      {
        code: 'PRC-2.6',
        name: 'Sell Ancillary Products',
        steps: [
          'Present Ancillary Offer',
          'Capture Ancillary Purchase',
          'Fulfil Ancillary Entitlement',
        ],
      },
    ],
  },
  {
    code: 'PRC-3',
    name: 'Serve the Customer',
    description: 'Look after the customer from booking through to post-trip resolution.',
    ownerPersonCode: 'PPL-0026',
    l2: [
      {
        code: 'PRC-3.1',
        name: 'Service Trip Changes',
        steps: ['Assess Change Eligibility', 'Reprice Trip', 'Reissue Documents', 'Confirm Change'],
      },
      { code: 'PRC-3.2', name: 'Handle Customer Contact' },
      {
        code: 'PRC-3.3',
        name: 'Communicate Proactively',
        steps: [
          'Detect Notifiable Event',
          'Select Channel & Audience',
          'Send Message',
          'Track Delivery & Response',
        ],
      },
      {
        code: 'PRC-3.4',
        name: 'Manage Loyalty Membership',
        steps: ['Enrol Member', 'Accrue Points', 'Redeem Award', 'Manage Tier Status'],
      },
      { code: 'PRC-3.5', name: 'Resolve Claims & Compensation' },
      { code: 'PRC-3.6', name: 'Manage Customer Consent & Preferences' },
    ],
  },
  {
    code: 'PRC-4',
    name: 'Operate the Flight',
    description: 'Plan, dispatch, monitor and close out every flight.',
    ownerPersonCode: 'PPL-0021',
    l2: [
      {
        code: 'PRC-4.1',
        name: 'Plan the Flight',
        steps: [
          'Build Operational Flight Plan',
          'Calculate Fuel Requirement',
          'Review Weather & NOTAMs',
          'Dispatch Release',
        ],
      },
      {
        code: 'PRC-4.2',
        name: 'Compute Weight & Balance',
        steps: ['Collect Load Data', 'Calculate Trim', 'Issue Loadsheet', 'Confirm Final Figures'],
      },
      {
        code: 'PRC-4.3',
        name: 'Control the Operation',
        steps: [
          'Monitor Flight Progress',
          'Assess Disruption Impact',
          'Decide Recovery Option',
          'Communicate Decision',
        ],
      },
      { code: 'PRC-4.4', name: 'Manage Datalink & Aircraft Messaging' },
      { code: 'PRC-4.5', name: 'Close Out the Flight' },
    ],
  },
  {
    code: 'PRC-5',
    name: 'Manage Crew',
    description: 'Plan, roster, track and recover crew within legality and agreement.',
    ownerPersonCode: 'PPL-0020',
    l2: [
      {
        code: 'PRC-5.1',
        name: 'Plan Crew Requirement',
        steps: ['Forecast Crew Demand', 'Plan Establishment', 'Plan Training Requirement'],
      },
      {
        code: 'PRC-5.2',
        name: 'Build Pairings & Rosters',
        steps: [
          'Generate Pairings',
          'Run Bid Process',
          'Generate Draft Roster',
          'Run Legality Check',
          'Publish Roster',
        ],
      },
      {
        code: 'PRC-5.3',
        name: 'Track Crew on the Day',
        steps: ['Monitor Crew Check-in', 'Detect Crew Shortfall', 'Track Duty & Rest'],
      },
      {
        code: 'PRC-5.4',
        name: 'Recover Disrupted Crew',
        steps: [
          'Identify Affected Pairings',
          'Generate Recovery Options',
          'Check Legality & Agreement',
          'Assign Replacement Crew',
          'Notify Crew & Operations',
          'Rebook Crew Travel & Hotel',
        ],
      },
      {
        code: 'PRC-5.5',
        name: 'Maintain Crew Qualifications',
        steps: ['Record Training Completion', 'Monitor Recency', 'Escalate Expiring Qualification'],
      },
      { code: 'PRC-5.6', name: 'Pay & Support Crew' },
    ],
  },
  {
    code: 'PRC-6',
    name: 'Handle the Airport & Ground',
    description: 'Accept, board and turn — the ninety minutes that decide on-time performance.',
    ownerPersonCode: 'PPL-0022',
    l2: [
      {
        code: 'PRC-6.1',
        name: 'Accept Passengers',
        steps: ['Verify Travel Documents', 'Assign Seat', 'Accept Baggage', 'Issue Boarding Pass'],
      },
      {
        code: 'PRC-6.2',
        name: 'Handle Baggage',
        steps: [
          'Reconcile Bag to Passenger',
          'Sort & Load Baggage',
          'Trace Mishandled Bag',
          'Deliver Recovered Bag',
        ],
      },
      {
        code: 'PRC-6.3',
        name: 'Board the Aircraft',
        steps: [
          'Sequence Boarding',
          'Verify Boarding Pass',
          'Reconcile Passenger Count',
          'Close the Flight',
        ],
      },
      {
        code: 'PRC-6.4',
        name: 'Turn the Aircraft',
        steps: ['Sequence Turn Tasks', 'Monitor Turn Milestones', 'Escalate Turn Delay'],
      },
      {
        code: 'PRC-6.5',
        name: 'Allocate Airport Resources',
        steps: ['Allocate Gate & Stand', 'Allocate Belt & Chute', 'Reallocate on Schedule Change'],
      },
      { code: 'PRC-6.6', name: 'Provide Special Assistance' },
    ],
  },
  {
    code: 'PRC-7',
    name: 'Maintain the Fleet',
    description: 'Keep aircraft airworthy and available to the schedule.',
    ownerPersonCode: 'PPL-0023',
    l2: [
      {
        code: 'PRC-7.1',
        name: 'Plan Maintenance',
        steps: ['Forecast Check Due', 'Plan Hangar Slot', 'Reserve Parts & Tooling'],
      },
      { code: 'PRC-7.2', name: 'Execute Line Maintenance' },
      { code: 'PRC-7.3', name: 'Manage Defects & Deferrals' },
      { code: 'PRC-7.4', name: 'Maintain Technical Records' },
      { code: 'PRC-7.5', name: 'Manage Parts & Inventory' },
    ],
  },
  {
    code: 'PRC-8',
    name: 'Manage Corporate Functions',
    description: 'Finance, people, sourcing, risk and the data that reports on them.',
    ownerPersonCode: 'PPL-0018',
    l2: [
      {
        code: 'PRC-8.1',
        name: 'Account for Revenue',
        steps: [
          'Capture Coupon Usage',
          'Prorate Interline Revenue',
          'Recognise Revenue',
          'Settle Interline',
        ],
      },
      {
        code: 'PRC-8.2',
        name: 'Process Payments & Settlement',
        steps: [
          'Authorise Payment',
          'Capture Funds',
          'Reconcile Merchant Settlement',
          'Handle Chargeback',
        ],
      },
      { code: 'PRC-8.3', name: 'Run Financial Planning & Reporting' },
      {
        code: 'PRC-8.4',
        name: 'Source & Manage Suppliers',
        steps: [
          'Qualify Supplier',
          'Award Contract',
          'Manage Obligations',
          'Review Supplier Performance',
        ],
      },
      { code: 'PRC-8.5', name: 'Manage People' },
      {
        code: 'PRC-8.6',
        name: 'Govern Data & Reporting',
        steps: [
          'Register Data Asset',
          'Define Quality Rule',
          'Monitor Quality Outcome',
          'Remediate Quality Issue',
        ],
      },
      { code: 'PRC-8.7', name: 'Manage Risk & Compliance' },
    ],
  },
];

export function buildProcesses(asOf: string, rng: Rng): Process[] {
  const processRng = rng.fork('processes');
  const stamp = provenanceFactory(asOf, rng.fork('processes-provenance'));
  const processes: Process[] = [];

  const push = (
    code: string,
    name: string,
    level: Process['level'],
    parentCode: string | null,
    description: string,
    ownerPersonCode: string | null,
    index: number,
  ): void => {
    processes.push({
      id: idFor('Process', code),
      businessCode: code,
      name,
      level,
      parentCode,
      description,
      ownerPersonCode,
      provenance: stamp(SOURCE_SYSTEMS.dataHub, `PROC-${pad(index, 5)}`, {
        // Process taxonomies are refreshed on a quarterly cadence, not nightly.
        meanAgeDays: level === 'L4' ? 48 : 24,
        meanConfidence: level === 'L1' ? 0.96 : level === 'L2' ? 0.9 : 0.74,
      }),
    });
  };

  let counter = 1;
  for (const l1 of L1_SEEDS) {
    push(l1.code, l1.name, 'L1', null, l1.description, l1.ownerPersonCode, counter);
    counter += 1;

    for (const l2 of l1.l2) {
      push(
        l2.code,
        l2.name,
        'L2',
        l1.code,
        `${l2.name} within ${l1.name}.`,
        l1.ownerPersonCode,
        counter,
      );
      counter += 1;

      const steps =
        l2.steps ??
        GENERIC_STEPS.slice(0, processRng.int(3, 4)).map(
          (verb) =>
            `${verb} ${l2.name.replace(/^(Manage|Handle|Build|Plan|Execute|Provide|Run|Account for|Process|Source & Manage|Govern|Develop|Construct & Present|Create & Manage|Distribute through|Communicate|Service|Resolve|Sell|Compute|Control|Track|Recover|Maintain|Allocate|Accept|Board|Turn|Close)\s+/, '')}`,
        );

      steps.forEach((stepName, stepIndex) => {
        const l3Code = `${l2.code}.${stepIndex + 1}`;
        push(
          l3Code,
          stepName,
          'L3',
          l2.code,
          `${stepName} — step ${stepIndex + 1} of ${l2.name}.`,
          null,
          counter,
        );
        counter += 1;

        const taskCount = processRng.int(2, 3);
        for (let t = 0; t < taskCount; t += 1) {
          const task = at(L4_TASKS, (stepIndex + t + counter) % L4_TASKS.length);
          const l4Code = `${l3Code}.${t + 1}`;
          push(
            l4Code,
            `${stepName}: ${task}`,
            'L4',
            l3Code,
            `${task} for ${stepName}.`,
            null,
            counter,
          );
          counter += 1;
        }
      });
    }
  }

  return processes;
}
